import { useEffect, useMemo } from "react";
import { useHostSession } from "@/hooks/useHostSession";
import { useProviderRuntime } from "@/hooks/useProviderRuntime";
import { useShellRuntime } from "@/shell/useShellRuntime";
import { useCaptainUpHostBridge } from "@/orchestrators/useCaptainUpHostBridge";
import { useEfRuntimeSupervisor } from "@/hooks/useEfRuntimeSupervisor";
import { useFeatureRuntime } from "@/features/runtime/useFeatureRuntime";
import useFeatureRegistry from "@/features/runtime/useFeatureRegistry";
import { useFeatureEvents } from "@/features/runtime/useFeatureEvents";
import type { EFFeatureRuntimeSnapshot } from "@/features/runtime/FeatureRuntimeTypes";
import { debugLog } from "@/utils/debug";

export function useEfAppRuntime() {
	const host = useHostSession();

	useEfRuntimeSupervisor(host.hostTransportReady);

	const providerRuntime = useProviderRuntime(
		host.providerBootEligible,
		host.providerBootInput
	);

	useCaptainUpHostBridge(
		providerRuntime.provider,
		providerRuntime.providerReady
	);

	const shell = useShellRuntime({
		hostTransportReady: host.hostTransportReady,
		hostConfigured: host.hostConfigured,
		hostGameReady: host.hostGameReady,
		providerBootEligible: host.providerBootEligible,
		provider: providerRuntime.provider,
		providerReady: providerRuntime.providerReady,
		providerRuntimeSnapshot: providerRuntime.providerRuntimeSnapshot,
		activeBootGeneration: providerRuntime.activeBootGeneration,
		readyBootGeneration: providerRuntime.readyBootGeneration,
		inFlightBootGeneration: providerRuntime.inFlightBootGeneration,
		requestFreshProviderBoot: providerRuntime.requestFreshProviderBoot,
	});

	const featureRegistry = useFeatureRegistry();

	const featureEvents = useFeatureEvents(
		providerRuntime.provider,
		providerRuntime.providerReady
	);

	const featureRuntime = useFeatureRuntime({
		host,
		providerRuntime,
		shell,
		featureEvents,
		featureRegistry: featureRegistry.registry,
	});

	const featureSnapshots = useMemo<readonly EFFeatureRuntimeSnapshot[]>(
		() =>
			featureRegistry.definitions.map((definition) => {
				const resolved = featureRegistry.resolveFeatureState(
					definition.id,
					featureRuntime
				);

				return Object.freeze({
					featureId: definition.id,
					eligibility: resolved.eligibility,
					activation: resolved.activation,
				});
			}),
		[featureRegistry, featureRuntime]
	);

	useEffect(() => {
		debugLog("[EF Runtime] composed runtime snapshot", {
			host,
			providerRuntime: {
				phase: providerRuntime.providerRuntimeSnapshot.phase,
				providerId: providerRuntime.providerRuntimeSnapshot.providerId,
			},
			shell: {
				popupVisible: shell.popupVisible,
				activeDockTarget: shell.activeDockTarget,
			},
			featureRuntime: {
				host: featureRuntime.view.host,
				provider: featureRuntime.view.provider,
				shell: featureRuntime.view.shell,
				selection: featureRuntime.view.selection,
			},
			featureEvents: featureEvents.getSnapshot(),
			featureSnapshots: featureSnapshots.map((snapshot) => ({
				featureId: snapshot.featureId,
				eligible: snapshot.eligibility.eligible,
				reasons: snapshot.eligibility.reasons,
				active: snapshot.activation.active,
				visible: snapshot.activation.visible,
			})),
		});
	}, [
		featureEvents,
		featureRuntime,
		featureSnapshots,
		host,
		providerRuntime,
		shell,
	]);

	return {
		host,
		providerRuntime,
		shell,
		featureEvents,
		featureRuntime,
		featureRegistry,
		featureSnapshots,
	};
}

export default useEfAppRuntime;